import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  Workflow, MousePointerClick, Sparkles, Check, ArrowRight, 
  Settings, Layout, Eye
} from 'lucide-react';
import PublicHeader from '@/components/PublicHeader';

export default function DragDropFormBuilderPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50">
      <PublicHeader breadcrumbs={[{ label: 'Features', link: '/how-it-works' }, { label: 'Drag & Drop Form Builder' }]} />

      <div className="pt-32">
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-br from-violet-600 to-indigo-600">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center text-white">
            <div className="w-20 h-20 rounded-2xl bg-white/10 backdrop-blur-sm flex items-center justify-center mx-auto mb-6">
              <Workflow className="w-10 h-10" />
            </div>
            <h1 className="text-5xl font-bold mb-4">Drag & Drop Form Builder</h1>
            <p className="text-xl text-violet-100 max-w-3xl mx-auto">
              Build beautiful, functional forms with an intuitive visual interface. No coding required.
            </p>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">How It Works</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Create professional forms in minutes with our visual builder
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Step 1 */}
            <div className="bg-white rounded-2xl border border-slate-200 p-8">
              <div className="w-12 h-12 rounded-xl bg-violet-100 text-violet-600 font-bold text-xl flex items-center justify-center mb-6">
                1
              </div>
              <div className="flex items-center gap-3 mb-4">
                <MousePointerClick className="w-6 h-6 text-violet-600" />
                <h3 className="text-xl font-bold text-slate-900">Drag Fields</h3>
              </div>
              <p className="text-slate-600 leading-relaxed">
                Choose from text inputs, dropdowns, checkboxes, date pickers, and more. Simply drag them onto your canvas.
              </p>
            </div>

            {/* Step 2 */}
            <div className="bg-white rounded-2xl border border-slate-200 p-8">
              <div className="w-12 h-12 rounded-xl bg-violet-100 text-violet-600 font-bold text-xl flex items-center justify-center mb-6">
                2
              </div>
              <div className="flex items-center gap-3 mb-4">
                <Settings className="w-6 h-6 text-violet-600" />
                <h3 className="text-xl font-bold text-slate-900">Configure</h3>
              </div>
              <p className="text-slate-600 leading-relaxed">
                Set labels, default values, validation rules, and conditional logic. Customize each field to match your needs.
              </p>
            </div>

            {/* Step 3 */}
            <div className="bg-white rounded-2xl border border-slate-200 p-8">
              <div className="w-12 h-12 rounded-xl bg-violet-100 text-violet-600 font-bold text-xl flex items-center justify-center mb-6">
                3
              </div>
              <div className="flex items-center gap-3 mb-4">
                <Eye className="w-6 h-6 text-violet-600" />
                <h3 className="text-xl font-bold text-slate-900">Preview & Deploy</h3>
              </div>
              <p className="text-slate-600 leading-relaxed">
                Preview your form, make adjustments, and deploy. Your form is ready to collect data and generate documents.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Supported Field Types */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Supported Field Types</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Everything you need to build comprehensive forms
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { name: 'Text Input', desc: 'Single-line text fields for names, emails, etc.' },
              { name: 'Text Area', desc: 'Multi-line text for descriptions and notes' },
              { name: 'Dropdown', desc: 'Select from predefined options' },
              { name: 'Checkbox', desc: 'Multiple selection options' },
              { name: 'Radio Buttons', desc: 'Single selection from multiple choices' },
              { name: 'Date Picker', desc: 'Calendar-based date selection' },
              { name: 'Number Input', desc: 'Numeric values with validation' },
              { name: 'Email Input', desc: 'Email fields with format validation' },
              { name: 'Phone Input', desc: 'Phone numbers with formatting' }
            ].map((field, index) => (
              <div key={index} className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                <h3 className="font-bold text-slate-900 mb-2">{field.name}</h3>
                <p className="text-sm text-slate-600">{field.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Key Benefits</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Build forms faster and smarter with visual tools
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center mb-4">
                <MousePointerClick className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="font-bold text-slate-900 mb-2">Visual Interface</h3>
              <p className="text-sm text-slate-600">No code - just drag and drop</p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center mb-4">
                <Layout className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="font-bold text-slate-900 mb-2">Flexible Layout</h3>
              <p className="text-sm text-slate-600">Organize fields your way</p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <div className="w-12 h-12 rounded-lg bg-violet-100 flex items-center justify-center mb-4">
                <Settings className="w-6 h-6 text-violet-600" />
              </div>
              <h3 className="font-bold text-slate-900 mb-2">Advanced Options</h3>
              <p className="text-sm text-slate-600">Validation, defaults, conditional logic</p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <div className="w-12 h-12 rounded-lg bg-orange-100 flex items-center justify-center mb-4">
                <Eye className="w-6 h-6 text-orange-600" />
              </div>
              <h3 className="font-bold text-slate-900 mb-2">Live Preview</h3>
              <p className="text-sm text-slate-600">See changes in real-time</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-slate-900 mb-6">Ready to Build Your First Form?</h2>
          <p className="text-lg text-slate-600 mb-8">
            Start creating beautiful forms with our drag & drop builder today
          </p>
          <Button
            onClick={() => navigate('/auth')}
            size="lg"
            className="h-14 px-10 rounded-full bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white text-lg font-bold"
          >
            Get Started Free
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>
      </div>
    </div>
  );
}
